import React, { useState } from 'react';
import {
  CheckCircle2, Clock, XCircle, Package, Truck, Archive, Loader2,
  ChevronDown, ChevronUp, FileText, Download, RotateCcw, StickyNote,
  MapPin, Mail, Phone, CreditCard, AlertTriangle, User, ClipboardList, FileDown,
} from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { downloadPickingSlip } from '@/lib/pickingSlips';
import { fmtDate } from '@/lib/utils';
import OrderInternalNotes from './OrderInternalNotes';
import OrderShipmentPanel from './OrderShipmentPanel';
import RoyalMailDispatchPanel from './RoyalMailDispatchPanel';
import OrderRefundPanel from './OrderRefundPanel';
import CreditNotesPanel from './CreditNotesPanel';
import OrderDownloadsPanel from './OrderDownloadsPanel';
import OrderAuditLogPanel from './OrderAuditLogPanel';
import OrderTypeIcon from './OrderTypeIcon';
import OrderMarginPanel from './OrderMarginPanel';
import { orderCostSummary, marginTone } from '@/lib/costMargin';
import { exportOrderMarginsCsv } from '@/utils/exportOrderMarginsCsv';

const STATUS_CONFIG = {
  pending:             { label: 'PENDING',        icon: Clock,        color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  paid:                { label: 'PAID',            icon: CheckCircle2, color: 'text-green-400',  bg: 'bg-green-400/10' },
  failed:              { label: 'FAILED',          icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-400/10' },
  in_production:       { label: 'IN PRODUCTION',   icon: Package,      color: 'text-blue-400',   bg: 'bg-blue-400/10' },
  packed:              { label: 'PACKED',          icon: Package,      color: 'text-indigo-400', bg: 'bg-indigo-400/10' },
  dispatched:          { label: 'DISPATCHED',      icon: Truck,        color: 'text-purple-400', bg: 'bg-purple-400/10' },
  delivered:           { label: 'DELIVERED',        icon: Archive,      color: 'text-primary',    bg: 'bg-primary/10' },
  completed:           { label: 'COMPLETED',       icon: CheckCircle2, color: 'text-primary',    bg: 'bg-primary/10' },
  refunded:            { label: 'REFUNDED',        icon: RotateCcw,    color: 'text-orange-400', bg: 'bg-orange-400/10' },
  partially_refunded:  { label: 'PART. REFUNDED',  icon: RotateCcw,    color: 'text-orange-400', bg: 'bg-orange-400/10' },
};

const ALL_STATUSES = ['pending', 'paid', 'failed', 'in_production', 'packed', 'dispatched', 'delivered', 'completed', 'refunded', 'partially_refunded'];

const TAB_ITEMS = [
  { key: 'shipment',  label: 'Shipping',  icon: Truck },
  { key: 'notes',     label: 'Notes',     icon: StickyNote },
  { key: 'refund',    label: 'Refund',    icon: RotateCcw },
  { key: 'downloads', label: 'Downloads', icon: Download },
  { key: 'audit',     label: 'Audit Log', icon: FileText },
];

export default function AdminOrderRow({ order, userEmail, selected, onToggleSelect }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null); // 'sent' | 'failed'
  const [activeTab, setActiveTab] = useState('shipment');
  const [refundTick, setRefundTick] = useState(0);
  const [pickingBusy, setPickingBusy] = useState(false);
  const { toast } = useToast();

  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  // COGS and gross margin, read from the cost snapshotted when the sale was made.
  const margin = orderCostSummary(order);

  const handleMarginCsv = () => {
    exportOrderMarginsCsv([order]);
    toast({ title: 'Margin CSV exported', description: `Cost and margin detail for order #${order.id?.slice(-8).toUpperCase()}.` });
  };

  const refreshOrders = () => queryClient.invalidateQueries({ queryKey: ['admin-orders', userEmail] });
  const handleRefunded = () => { refreshOrders(); setRefundTick((t) => t + 1); };

  const handlePickingSlip = async () => {
    setPickingBusy(true);
    try {
      await downloadPickingSlip(order.id);
    } catch (e) {
      toast({ title: 'Picking slip failed', description: e.message, variant: 'destructive' });
    }
    setPickingBusy(false);
  };

  const handleSendStatusEmail = async () => {
    setSendingEmail(true);
    setEmailResult(null);
    try {
      await base44.functions.invoke('orderStatusEmail', { manual: true, orderId: order.id });
      setEmailResult('sent');
    } catch {
      setEmailResult('failed');
    }
    setSendingEmail(false);
    setTimeout(() => setEmailResult(null), 5000);
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setSaving(true);
    await base44.functions.invoke('adminUpdateOrder', {
      orderId: order.id,
      patch: { status: newStatus },
      auditAction: 'status_change',
      auditDescription: `Status changed to ${newStatus}`,
      auditOldValue: order.status,
      auditNewValue: newStatus,
    });
    await refreshOrders();
    setSaving(false);
  };

  const deliveryLines = [
    order.delivery_address_line1,
    order.delivery_address_line2,
    [order.delivery_city, order.delivery_postcode].filter(Boolean).join(', '),
  ].filter(Boolean);

  return (
    <div className="border-b border-white/5">
      {/* Single-line row */}
      <div
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 hover:bg-white/5 transition-colors cursor-pointer"
      >
        <span className="w-8 shrink-0 flex justify-start" onClick={(e) => e.stopPropagation()}>
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelect(order.id)}
              className="w-4 h-4 accent-primary cursor-pointer"
            />
          )}
        </span>
        <span className="w-6 shrink-0 flex justify-center" title={`${order.source || 'Unknown'}`}>
          <OrderTypeIcon order={order} sourceLabel={order.source} />
        </span>
        <span className="text-white/70 text-xs font-mono font-bold w-16 sm:w-20 shrink-0">
          #{order.id?.slice(-8).toUpperCase()}
        </span>
        <span className="text-white/40 text-xs w-20 shrink-0 hidden md:block">
          {fmtDate(order.created_date) || '—'}
        </span>
        <span className="text-white text-sm font-medium flex-1 min-w-0 truncate">
          {order.customer_name || '—'}
        </span>
        <span className="text-white/50 text-sm flex-1 min-w-0 truncate hidden sm:block">
          {order.product_name || '—'}
          {order.is_consolidated && (
            <span className="ml-2 text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 align-middle">CONSOLIDATED</span>
          )}
          {order.has_delayed_line && (
            <span className="ml-2 text-[9px] font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 align-middle">SPLIT DELIVERY</span>
          )}
        </span>
        <span className="w-16 sm:w-20 text-right shrink-0">
          <span className="text-primary font-black text-sm block">£{Number(order.amount || 0).toFixed(2)}</span>
          {margin.marginPct != null && (
            <span className={`text-[9px] block md:hidden ${marginTone(margin.marginPct)}`}>
              {margin.marginPct.toFixed(0)}%
            </span>
          )}
        </span>
        <span className="hidden md:block w-28 text-right shrink-0">
          {margin.cogs == null ? (
            <span className="text-[9px] font-bold tracking-wider text-white/25">
              {margin.missingCost ? 'NO COST' : '—'}
            </span>
          ) : (
            <>
              <span className={`text-[10px] block ${marginTone(margin.marginPct)}`}>
                GM £{margin.margin.toFixed(2)}
              </span>
              <span className="text-[9px] text-white/30 block">
                COGS £{margin.cogs.toFixed(2)}{margin.marginPct == null ? '' : ` · ${margin.marginPct.toFixed(0)}%`}
              </span>
            </>
          )}
        </span>
        <span className={`flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-2 sm:px-2.5 py-1 w-8 sm:w-32 justify-center shrink-0 ${status.bg} ${status.color}`}>
          <StatusIcon className="w-3 h-3" /> <span className="hidden sm:inline">{status.label}</span>
        </span>
        <span className="w-6 shrink-0 text-white/30">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </div>

      {/* Expanded detail */}
      {open && (
        <div className="border-t border-white/5 bg-[#0a0a0c]">
          {/* Line items — a basket sale holds every line on one order */}
          {Array.isArray(order.line_items) && order.line_items.length > 0 && (
            <div className="p-5 pb-0">
              <p className="text-primary text-[10px] font-bold tracking-widest mb-3">ORDER ITEMS</p>
              <div className="border border-white/5 overflow-hidden">
                <div className="flex items-center gap-3 px-3 py-2 bg-[#111115] border-b border-white/5">
                  <p className="text-white/30 text-[10px] font-bold tracking-widest w-24 shrink-0">CODE</p>
                  <p className="text-white/30 text-[10px] font-bold tracking-widest flex-1">DESCRIPTION</p>
                  <p className="text-white/30 text-[10px] font-bold tracking-widest w-12 text-center shrink-0">QTY</p>
                  <p className="text-white/30 text-[10px] font-bold tracking-widest w-20 text-right shrink-0">UNIT</p>
                  <p className="text-white/30 text-[10px] font-bold tracking-widest w-20 text-right shrink-0">TOTAL</p>
                </div>
                {order.line_items.map((li, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 border-b border-white/5 last:border-b-0">
                    <p className="text-white/50 text-xs font-mono w-24 shrink-0 truncate">{li.product_code || '—'}</p>
                    <p className="text-white/80 text-xs flex-1 min-w-0 truncate">
                      {li.description}
                      {li.delayed && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 align-middle">
                          <Clock className="w-2.5 h-2.5" /> SHIPS LATER
                        </span>
                      )}
                    </p>
                    <p className="text-white/60 text-xs w-12 text-center shrink-0">{li.qty}</p>
                    <p className="text-white/60 text-xs w-20 text-right shrink-0">£{Number(li.unit_price_net || 0).toFixed(2)}</p>
                    <p className="text-white text-xs font-bold w-20 text-right shrink-0">£{Number(li.gross || 0).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <p className="text-white/25 text-[10px] mt-2">
                Unit prices ex-VAT. Postage is charged once for the whole sale.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-5">
            {/* Customer info */}
            <div className="lg:col-span-1 space-y-4">
              <div>
                <p className="text-primary text-[10px] font-bold tracking-widest mb-3">CUSTOMER</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-3.5 h-3.5 text-white/30 shrink-0" />
                    <span className="text-white font-medium truncate">{order.customer_name || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Mail className="w-3.5 h-3.5 text-white/30 shrink-0" />
                    <span className="text-white/60 truncate">{order.customer_email || '—'}</span>
                  </div>
                  {order.customer_phone && (
                    <div className="flex items-center gap-2 text-xs">
                      <Phone className="w-3.5 h-3.5 text-white/30 shrink-0" />
                      <span className="text-white/60">{order.customer_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {deliveryLines.length > 0 && (
                <div>
                  <p className="text-primary text-[10px] font-bold tracking-widest mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> DELIVERY ADDRESS
                  </p>
                  <div className="space-y-0.5">
                    {deliveryLines.map((line, i) => (
                      <p key={i} className="text-white/60 text-xs">{line}</p>
                    ))}
                  </div>
                </div>
              )}

              {order.notes && (
                <div>
                  <p className="text-primary text-[10px] font-bold tracking-widest mb-2">ORDER NOTES</p>
                  <p className="text-white/60 text-xs leading-relaxed">{order.notes}</p>
                </div>
              )}

              <div className="pt-2 border-t border-white/5 space-y-1.5">
                {order.square_payment_id && (
                  <div className="flex items-center gap-2 text-xs">
                    <CreditCard className="w-3.5 h-3.5 text-white/30 shrink-0" />
                    <span className="text-white/30">Payment:</span>
                    <span className="text-white/60 font-mono truncate">{order.square_payment_id}</span>
                  </div>
                )}
                {order.invoice_url && (
                  <a href={order.invoice_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-primary hover:text-primary/80 transition-colors">
                    <FileText className="w-3.5 h-3.5" /> View invoice (PDF)
                  </a>
                )}
                {order.voided_invoice_numbers?.length > 0 && (
                  <p className="text-white/30 text-xs">
                    Invoice(s) voided on merge: <span className="font-mono">{order.voided_invoice_numbers.join(', ')}</span>
                  </p>
                )}
                <button
                  onClick={handlePickingSlip}
                  disabled={pickingBusy}
                  title="Generate a warehouse picking slip for this order"
                  className="flex items-center gap-2 text-xs text-white/60 hover:text-primary transition-colors disabled:opacity-50"
                >
                  {pickingBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />} Picking slip (PDF)
                </button>
                <button
                  onClick={handleMarginCsv}
                  title="Download this sale's COGS and gross margin as a CSV"
                  className="flex items-center gap-2 text-xs text-white/60 hover:text-primary transition-colors"
                >
                  <FileDown className="w-3.5 h-3.5" /> Margin CSV
                </button>
                {order.tracking_number && (
                  <p className="text-white/40 text-xs">Tracking: {order.tracking_number}</p>
                )}
                {order.reconciled && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold text-yellow-400 bg-yellow-400/10 px-2 py-0.5">
                    <AlertTriangle className="w-2.5 h-2.5" /> RECONCILED
                  </span>
                )}
              </div>

              {/* Status changer */}
              <div className="pt-2">
                <p className="text-primary text-[10px] font-bold tracking-widest mb-2">STATUS</p>
                <div className="flex items-center gap-2">
                  <select
                    value={order.status}
                    onChange={handleStatusChange}
                    disabled={saving}
                    className="flex-1 bg-[#111115] border border-white/10 text-white text-xs font-bold h-9 px-3 focus:outline-none focus:border-primary/60 transition-colors disabled:opacity-50"
                  >
                    {ALL_STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-[#111115]">
                        {STATUS_CONFIG[s]?.label || s}
                      </option>
                    ))}
                  </select>
                  {saving && <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />}
                </div>
                <button
                  onClick={handleSendStatusEmail}
                  disabled={sendingEmail || saving}
                  className="mt-2 flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/60 hover:text-primary text-[10px] font-bold tracking-wider px-3 py-1.5 transition-all disabled:opacity-40"
                >
                  {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                  SEND STATUS EMAIL
                </button>
                {emailResult === 'sent' && (
                  <p className="mt-1.5 text-green-400 text-[10px] font-bold tracking-wider">✓ Email sent to customer</p>
                )}
                {emailResult === 'failed' && (
                  <p className="mt-1.5 text-red-400 text-[10px] font-bold tracking-wider">✗ Failed to send email</p>
                )}
              </div>

              <OrderMarginPanel order={order} />
            </div>

            {/* Management panels */}
            <div className="lg:col-span-2">
              <div className="flex overflow-x-auto border-b border-white/5">
                {TAB_ITEMS.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-[10px] font-bold tracking-wider whitespace-nowrap transition-colors ${
                        activeTab === tab.key ? 'text-primary border-b-2 border-primary' : 'text-white/30 hover:text-white/60'
                      }`}
                    >
                      <TabIcon className="w-3 h-3" /> {tab.label}
                    </button>
                  );
                })}
              </div>
              <div className="p-4">
                {activeTab === 'shipment'  && (
                  <div className="space-y-3">
                    <RoyalMailDispatchPanel order={order} onSaved={refreshOrders} />
                    <OrderShipmentPanel orderId={order.id} onSaved={refreshOrders} />
                  </div>
                )}
                {activeTab === 'notes'     && <OrderInternalNotes orderId={order.id} />}
                {activeTab === 'refund'    && (
                  <>
                    <CreditNotesPanel orderId={order.id} refreshSignal={refundTick} />
                    <OrderRefundPanel order={order} onRefunded={handleRefunded} />
                  </>
                )}
                {activeTab === 'downloads' && <OrderDownloadsPanel orderId={order.id} customerEmail={order.customer_email} />}
                {activeTab === 'audit'     && <OrderAuditLogPanel orderId={order.id} />}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}